import React, { useState } from "react";
import { Typography, Chip } from "@material-ui/core";
import { Card, CardContent } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { formatter } from "../../utils/formatter";
import { paymentStatus } from "../../models/paymentStatus";
import Pagination from '@material-ui/lab/Pagination';
import usePagination from "../../utils/pagination";
import moment from "moment";

/**
 * The payments this payout is made of.
 *
 * Replaces the separate Invoice Payments and Deposits tables. A disbursement is
 * a payout of money we were holding, and `payments` is the only record that
 * knows whether we were holding it — a front-desk card taking and a booking-
 * engine card payment look identical everywhere else.
 *
 * The gateway order id is shown because it is the one value that can be pasted
 * into the Midtrans dashboard to check a line against its source.
 */

const useStyles = makeStyles(() => ({
    textBold: {
        fontWeight: "bold",
    },
}));

const PaymentInfo = ({ disbursement }) => {
    const classes = useStyles();

    const rows = disbursement?.payments ?? [];

    const [page, setPage] = useState(1);
    const PER_PAGE = 10;

    const count = Math.ceil(rows.length / PER_PAGE);
    const _DATA = usePagination(rows, PER_PAGE);

    const handleChange = (e, p) => {
        setPage(p);
        _DATA.jump(p);
    };

    return (
        <Card p="5">
            <CardContent>
                <Grid item xs={12}>
                    <Typography variant="h6" className={classes.textBold} gutterBottom>
                        Payments
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Payment No</TableCell>
                                    <TableCell>Paid With</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="right">Gateway Fee</TableCell>
                                    <TableCell align="right">Platform Fee</TableCell>
                                    <TableCell>Gateway Order Id</TableCell>
                                    <TableCell>Created Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {_DATA.currentData().map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatter.paymentNo(row.payment_no)}</TableCell>
                                        <TableCell>{row.e_payment_types?.comment ?? row.payment_type ?? "—"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.e_payment_status?.comment ?? row.status}
                                                style={{
                                                    color: 'white',
                                                    background: paymentStatus.getColor(row.e_payment_status?.id ?? row.status),
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatter.formatMoney(row.amount, row.received_currency, 2)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.gateway_fee != null
                                                ? formatter.formatMoney(row.gateway_fee, row.received_currency, 2)
                                                : "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.platform_fee != null
                                                ? formatter.formatMoney(row.platform_fee, row.received_currency, 2)
                                                : "—"}
                                        </TableCell>
                                        <TableCell>{row.gateway_order_id}</TableCell>
                                        <TableCell>
                                            {row.created_at != null
                                                ? moment(row.created_at).format("DD MMM YYYY hh:mm:ss A")
                                                : ""}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            count={count}
                            size="large"
                            page={page}
                            variant="outlined"
                            shape="rounded"
                            onChange={handleChange}
                            style={{ float: "right" }}
                        />
                    </TableContainer>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default PaymentInfo;
